import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
    CreateRequirementFieldRequest,
    FieldAssignment,
    ProductSummary,
    RequirementEntityType,
    RequirementField,
    RequirementFieldOptionSource,
    RequirementFieldType,
    RequirementGroupPlacement,
} from '../product-requirements-config.dto';
import ConditionBuilder, { type FieldOption } from './ConditionBuilder';
import ListSizeEditor from './ListSizeEditor';
import OptionsEditor from './OptionsEditor';
import ProductAssignmentsEditor, { type AssignmentDraft } from './ProductAssignmentsEditor';

const FIELD_TYPES: RequirementFieldType[] = ['text', 'number', 'document', 'date', 'boolean', 'select', 'list'];
// SERVICE-entity values are plain text in the backend store — no documents, no LIST rows.
const SERVICE_FIELD_TYPES: RequirementFieldType[] = ['text', 'number', 'date', 'boolean', 'select'];
const ENTITY_TYPES: RequirementEntityType[] = ['real_estate_asset', 'building'];
const OPTION_SOURCES: RequirementFieldOptionSource[] = ['static', 'buildings', 'services'];

interface Props {
    open: boolean;
    mode: 'create' | 'edit';
    /** Placement of the owning group — drives the entity-type default and choices (GEN-591). */
    groupPlacement?: RequirementGroupPlacement;
    /** When creating a sub-field under a LIST parent. */
    parentFieldId?: string | null;
    /** Existing field being edited (mode === 'edit'). */
    field?: RequirementField | null;
    /** Sibling field codes the conditions may reference (group fields minus self). */
    fieldOptions: FieldOption[];
    /** display_order to assign on create. */
    nextDisplayOrder: number;
    /** Products + this field's current assignments (edit mode, top-level only). */
    products: ProductSummary[];
    assignments: FieldAssignment[];
    onClose: () => void;
    /**
     * `assignmentDrafts` is non-null only in edit mode for top-level fields —
     * the page diffs it against the server assignments and persists the delta.
     */
    onSubmit: (request: CreateRequirementFieldRequest, assignmentDrafts: Map<string, AssignmentDraft> | null) => void;
    saving?: boolean;
}

// Criticality is intentionally absent: it is per-product and lives on the
// assignment (GEN-642) — see the "Assigned products" section below.
function emptyDraft(
    parentFieldId: string | null | undefined,
    displayOrder: number,
    groupPlacement: RequirementGroupPlacement,
): CreateRequirementFieldRequest {
    return {
        code: '',
        name: '',
        label: '',
        placeholder: '',
        description: '',
        fieldType: 'text',
        required: false,
        displayOrder,
        // Placement and entity type imply each other (GEN-591), so the group
        // decides — the backend rejects mismatches.
        entityType: groupPlacement === 'service_information' ? 'service' : 'real_estate_asset',
        targetFolder: null,
        visibleWhen: null,
        requiredWhen: null,
        parentFieldId: parentFieldId ?? null,
        multiline: false,
        minRows: null,
        multiple: false,
        optionSource: null,
        options: [],
        listSize: null,
    };
}

function draftFromField(field: RequirementField): CreateRequirementFieldRequest {
    return {
        code: field.code,
        name: field.name,
        label: field.label,
        placeholder: field.placeholder ?? '',
        description: field.description ?? '',
        fieldType: field.fieldType,
        required: field.required,
        displayOrder: field.displayOrder,
        entityType: field.entityType,
        targetFolder: field.targetFolder,
        visibleWhen: field.visibleWhen,
        requiredWhen: field.requiredWhen,
        parentFieldId: field.parentFieldId,
        multiline: field.multiline,
        minRows: field.minRows,
        multiple: field.multiple,
        optionSource: field.optionSource,
        options: field.options ?? [],
        listSize: field.listSize,
    };
}

/** Drop config that doesn't apply to the chosen type so the backend doesn't reject it. */
function sanitize(draft: CreateRequirementFieldRequest): CreateRequirementFieldRequest {
    const next = { ...draft };
    if (next.fieldType !== 'list') {
        next.listSize = null;
    }
    if (next.fieldType !== 'select') {
        next.optionSource = null;
        next.options = null;
        next.multiple = false;
    }
    if (next.fieldType !== 'text') {
        next.multiline = false;
    }
    return next;
}

export default function FieldEditorDialog(props: Props) {
    const { t } = useTranslation();
    const { open, mode, field, parentFieldId, fieldOptions, products, assignments } = props;
    const groupPlacement = props.groupPlacement ?? 'data_collection';

    const [draft, setDraft] = useState<CreateRequirementFieldRequest>(
        mode === 'edit' && field
            ? draftFromField(field)
            : emptyDraft(parentFieldId, props.nextDisplayOrder, groupPlacement),
    );
    // Per-product assignment drafts (edit mode) — committed in a batch on Save.
    const [assignmentDrafts, setAssignmentDrafts] = useState<Map<string, AssignmentDraft>>(
        () =>
            new Map(
                assignments.map((a) => [
                    a.productId,
                    { assigned: true, dataCriticality: a.dataCriticality, criticalityWhen: a.criticalityWhen },
                ]),
            ),
    );

    const set = (patch: Partial<CreateRequirementFieldRequest>) => setDraft((d) => ({ ...d, ...patch }));

    const isSubField = (draft.parentFieldId ?? null) != null;
    const showAssignments = mode === 'edit' && !isSubField;
    const canSave = draft.code.trim() !== '' && draft.name.trim() !== '' && draft.label.trim() !== '';

    return (
        <Dialog open={open} onClose={props.onClose} maxWidth="md" fullWidth>
            <DialogTitle>{mode === 'create' ? t('Add field') : t('Edit field')}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label={t('Code')}
                                helperText={
                                    mode === 'edit'
                                        ? t('Cannot be changed — conditions and collected answers reference it.')
                                        : t('Stable identifier — conditions reference it.')
                                }
                                value={draft.code}
                                slotProps={{ input: { readOnly: mode === 'edit' } }}
                                onChange={(e) => mode === 'create' && set({ code: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth size="small" required>
                                <InputLabel>{t('Type')}</InputLabel>
                                <Select
                                    label={t('Type')}
                                    value={draft.fieldType}
                                    onChange={(e) => set({ fieldType: e.target.value as RequirementFieldType })}
                                >
                                    {(draft.entityType === 'service' ? SERVICE_FIELD_TYPES : FIELD_TYPES).map(
                                        (type) => (
                                            <MenuItem key={type} value={type}>
                                                {t(type)}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label={t('Name')}
                                helperText={t('Singular noun (used for LIST row titles).')}
                                value={draft.name}
                                onChange={(e) => set({ name: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label={t('Label')}
                                value={draft.label}
                                onChange={(e) => set({ label: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('Placeholder')}
                                value={draft.placeholder ?? ''}
                                onChange={(e) => set({ placeholder: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('Description')}
                                value={draft.description ?? ''}
                                onChange={(e) => set({ description: e.target.value })}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('Belongs to')}</InputLabel>
                                <Select
                                    label={t('Belongs to')}
                                    value={draft.entityType}
                                    onChange={(e) => set({ entityType: e.target.value as RequirementEntityType })}
                                >
                                    {(groupPlacement === 'service_information'
                                        ? ['service' as const]
                                        : ENTITY_TYPES
                                    ).map((et) => (
                                        <MenuItem key={et} value={et}>
                                            {t(et)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={draft.required}
                                        onChange={(e) => set({ required: e.target.checked })}
                                    />
                                }
                                label={t('Required')}
                            />
                        </Grid>
                    </Grid>

                    {/* Type-specific config */}
                    {draft.fieldType === 'text' && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={draft.multiline}
                                    onChange={(e) => set({ multiline: e.target.checked })}
                                />
                            }
                            label={t('Multi-line text')}
                        />
                    )}

                    {draft.fieldType === 'select' && (
                        <>
                            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>{t('Options source')}</InputLabel>
                                    <Select
                                        label={t('Options source')}
                                        value={draft.optionSource ?? 'static'}
                                        onChange={(e) =>
                                            set({ optionSource: e.target.value as RequirementFieldOptionSource })
                                        }
                                    >
                                        {OPTION_SOURCES.map((s) => (
                                            <MenuItem key={s} value={s}>
                                                {t(s)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={draft.multiple}
                                            onChange={(e) => set({ multiple: e.target.checked })}
                                        />
                                    }
                                    label={t('Allow multiple')}
                                />
                            </Stack>
                            {(draft.optionSource ?? 'static') === 'static' && (
                                <OptionsEditor value={draft.options ?? []} onChange={(options) => set({ options })} />
                            )}
                        </>
                    )}

                    {draft.fieldType === 'list' && (
                        <ListSizeEditor
                            value={draft.listSize ?? null}
                            fieldOptions={fieldOptions}
                            onChange={(listSize) => set({ listSize })}
                        />
                    )}

                    <Divider />

                    <ConditionBuilder
                        title={t('Show this field when…')}
                        helperText={t('Leave off to always show the field.')}
                        value={draft.visibleWhen ?? null}
                        fieldOptions={fieldOptions}
                        onChange={(visibleWhen) => set({ visibleWhen })}
                    />
                    <ConditionBuilder
                        title={t('Require this field when…')}
                        helperText={t('Adds a conditional requirement on top of the Required checkbox.')}
                        value={draft.requiredWhen ?? null}
                        fieldOptions={fieldOptions}
                        onChange={(requiredWhen) => set({ requiredWhen })}
                    />

                    {/* Assignment + per-product criticality (top-level fields only;
                        sub-fields inherit, create assigns after save) */}
                    {showAssignments && (
                        <>
                            <Divider />
                            <ProductAssignmentsEditor
                                products={products}
                                value={assignmentDrafts}
                                field={{ code: draft.code, fieldType: draft.fieldType }}
                                fieldOptions={fieldOptions}
                                disabled={props.saving}
                                onChange={(productId, next) =>
                                    setAssignmentDrafts((m) => new Map(m).set(productId, next))
                                }
                            />
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>{t('Cancel')}</Button>
                <Button
                    variant="contained"
                    disabled={!canSave || props.saving}
                    onClick={() => props.onSubmit(sanitize(draft), showAssignments ? assignmentDrafts : null)}
                >
                    {t('Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
